'use strict';

// external imports

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import {
    PropertyKeyType,
    PropertyValueType,
    PropertySetValue
} from '~/shared/src/mm/PropertySet';
import PropertyType from '~/shared/src/mm/PropertyType';
import Status from '~/shared/src/mm/Status';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import ValueSet from '~/shared/src/mm/ValueSet';
import Value from '~/shared/src/mm/Value';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {__} from '~/shared/src/utilities/messageCatalog';
import {
    convertValueForType,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';
import {validateValueForType} from '~/shared/src/utilities/valueValidators';

// backend imports


// type definitions
export type ReferencePropertySetAttributes = {
    id? : number,
    referenceSetId : number,
    propertyTypeId : number,
    valueSetId? : number,
    statusId? : number,
    origin? : string,
    module? : string,
    description? : string,
    keyType? : PropertyKeyType,
    keyDefinition? : string,
    keyExample? : string,
    valueType? : PropertyValueType,
    valueDefinition? : string,
    valueExample? : string,
    valueDefault? : string,
    valueMin? : string,
    valueMax? : string,
    valueUnit? : string,
    isEnumerated? : boolean,
    isImported? : boolean,
    uuid? : string,
    modifierUserId? : string,
    modificationTime? : NDate,
    validFromTime? : NDate,
    validToTime? : NDate
};

export type ReferencePropertySetAttributesCollection = Array<ReferencePropertySetAttributes>;

export type ReferencePropertySetAttributesRequired = keyof Pick<ReferencePropertySetAttributes, 'id' | 'referenceSetId' | 'propertyTypeId' | 'statusId'>;

export type ReferencePropertySetAttributesSettable = Pick<ReferencePropertySetAttributes, 'valueSetId' | 'statusId' | 'origin' | 'module' | 'description' | 'keyType' | 'keyDefinition' | 'keyExample' | 'valueType' | 'valueDefinition' | 'valueExample' | 'valueDefault' | 'valueMin' | 'valueMax' | 'valueUnit' | 'isEnumerated' | 'isImported'>;


/**
 * The ReferencePropertySet class.
 */
class ReferencePropertySet {

    /**
     * The unique Id counter for ReferencePropertySets
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the ReferencePropertySet
     */
    readonly id : number;

    /**
     * The ReferenceSetID of the ReferencePropertySet
     */
    readonly referenceSetId : number;

    /**
     * The PropertyTypeID of the ReferencePropertySet
     */
    readonly propertyTypeId : number;

    /**
     * The ValueSetID of the ReferencePropertySet
     */
    protected _valueSetId : number = 0;
    get valueSetId() : number {
        return this._valueSetId;
    }

    set valueSetId( value : number ) {
        this.checkStatus();

        this.handleModified( 'valueSetId', value );

        this._valueSetId = value;
    }

    /**
     * The StatusID of the ReferencePropertySet
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        this.validateStatusId( value );

        this.handleModified( 'statusId', value );

        this._statusId = value;
    }

    /**
     * The origin of this
     */
    protected _origin : string = 'FSZ';
    get origin() : string {
        return this._origin;
    }

    set origin( value : string ) {
        this.validateOrigin( value );

        this.handleModified( 'origin', value );

        this._origin = value;
    }

    /**
     * The module this belongs to
     * @protected
     */
    protected _module : string = 'base';
    get module() : string {
        return this._origin;
    }

    set module( value : string ) {
        this.validateModule( value );

        this.handleModified( 'module', value );

        this._module = value;
    }

    /**
     * The description of the ReferencePropertySet
     */
    protected _description : string = '';
    get description() {
        return this._description;
    }

    set description( value : string ) {
        this.checkStatus();

        this.handleModified( 'description', value );

        this._description = value;
    }

    /**
     * The type of the key of an Item of the ReferencePropertySet
     */
    protected _keyType : PropertyKeyType = 'STRING';
    get keyType() : PropertyKeyType {
        return this._keyType;
    }

    set keyType( value : PropertyKeyType ) {
        this.checkStatus();

        this.handleModified( 'keyType', value );

        this._keyType = value;
    }

    /**
     * The definition of the key of an Item of the ReferencePropertySet
     */
    protected _keyDefinition : string = '';
    get keyDefinition() {
        return this._keyDefinition;
    }

    set keyDefinition( value : string ) {
        this.validateKeyDefinition( value );

        this.handleModified( 'keyDefinition', value );

        this._keyDefinition = value;

        this.parseKeyDefinition();
    }

    /**
     * The example of the key of an Item of the ReferencePropertySet
     */
    protected _keyExample : string = '';
    get keyExample() {
        return this._keyExample;
    }

    set keyExample( value : string ) {
        this.checkStatus();

        this.handleModified( 'keyExample', value );

        this._keyExample = value;
    }

    /**
     * The type of the value of an Item of the ReferencePropertySet
     */
    protected _valueType : PropertyValueType = 'STRING';
    get valueType() : PropertyValueType {
        return this._valueType;
    }

    set valueType( value : PropertyValueType ) {
        this.checkStatus();

        this.handleModified( 'valueType', value );

        this._valueType = value;
    }

    /**
     * The definition of the value of an Item of the ReferencePropertySet
     */
    protected _valueDefinition : string = '';
    get valueDefinition() {
        return this._valueDefinition;
    }

    set valueDefinition( value : string ) {
        this.validateValueDefinition( value );

        this.handleModified( 'valueDefinition', value );

        this._valueDefinition = value;

        this.parseValueDefinition();
    }

    /**
     * The example of the value of an Item of the ReferencePropertySet
     */
    protected _valueExample : string = '';
    get valueExample() {
        return this._valueExample;
    }

    set valueExample( value : string ) {
        this.checkStatus();

        this.handleModified( 'valueExample', value );

        this._valueExample = value;
    }

    /**
     * The default of the value of an Item of the ReferencePropertySet
     */
    protected _valueDefault : string = '';
    get valueDefault() : AttributeValue {
        return this._valueDefault !== '' ? convertValueForType( this._valueDefault, this._valueType ) : '';
    }

    set valueDefault( value : AttributeValue ) {
        this.validateDefaultValue( value );

        this.handleModified( 'valueDefault', value );

        this._valueDefault = convertValueToString( value, this._valueType );
    }

    /**
     * The min of the value of an Item of the ReferencePropertySet
     */
    protected _valueMin : string = '';
    get valueMin() {
        return this._valueMin;
    }

    set valueMin( value : string ) {
        this.checkStatus();

        this.handleModified( 'valueMin', value );

        this._valueMin = value;
    }

    /**
     * The max of the value of an Item of the ReferencePropertySet
     */
    protected _valueMax : string = '';
    get valueMax() {
        return this._valueMax;
    }

    set valueMax( value : string ) {
        this.checkStatus();

        this.handleModified( 'valueMax', value );

        this._valueMax = value;
    }

    /**
     * The unit of the value of an Item of the ReferencePropertySet
     */
    protected _valueUnit : string = '';
    get valueUnit() {
        return this._valueUnit;
    }

    set valueUnit( value : string ) {
        this.checkStatus();

        this.handleModified( 'valueUnit', value );

        this._valueUnit = value;
    }

    /**
     * Is the ReferencePropertySet enumerated
     */
    protected _isEnumerated : boolean = true;
    get isEnumerated() {
        return this._isEnumerated;
    }

    set isEnumerated( value : boolean ) {
        this.checkStatus();

        this.handleModified( 'isEnumerated', value );

        this._isEnumerated = value;

        this.parseKeyDefinition();
        this.parseValueDefinition();
    }

    /**
     * Is the ReferencePropertySet imported from another datasource
     */
    protected _isImported : boolean = false;
    get isImported() {
        return this._isImported;
    }

    set isImported( value : boolean ) {
        this.checkStatus();

        this.handleModified( 'isImported', value );

        this._isImported = value;
    }

    /**
     * The unique spoken id of the ReferencePropertySet
     */
    readonly uuid : string = '';

    /**
     * The UserID of the last modifier of the ReferencePropertySet
     */
    readonly modifierUserId : string = '';

    /**
     * The last modification date of the ReferencePropertySet
     */
    readonly modificationTime : NDate = null;

    /**
     * The valid from date of a History-ReferencePropertySet
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-ReferencePropertySet
     */
    readonly validToTime : NDate = null;

    /**
     * The template string for resolving the string for the key of a property.
     */
    private _keyToStringTemplate? : string;
    get keyToStringTemplate() : string {
        return this._keyToStringTemplate!;
    }

    /**
     * The regular expression to extract the key and the orderNum if from the string for the key.
     */
    private _keyFromStringRegExp? : string;
    get keyFromStringRegExp() : RegExp | undefined {
        if ( this._keyFromStringRegExp ) {
            return new RegExp( this._keyFromStringRegExp, 'g' );
        }
        else {
            return undefined;
        }
    }

    /**
     * The template string for resolving the string for the value of a property.
     */
    private _valueToStringTemplate? : string;
    get valueToStringTemplate() : string {
        return this._valueToStringTemplate!;
    }

    /**
     * The regular expression to extract the value and the orderNum if from the string for the value.
     */
    private _valueFromStringRegExp? : string;
    get valueFromStringRegExp() : RegExp | undefined {
        if ( this._valueFromStringRegExp ) {
            return new RegExp( this._valueFromStringRegExp, 'g' );
        }
        else {
            return undefined;
        }
    }

    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    get referenceSet() : ReferenceSet {
        return metaModel[ this._schema ].getReferenceSet( this.referenceSetId )!;
    }

    get propertyType() : PropertyType {
        return metaModel[ this._schema ].getPropertyType( this.propertyTypeId )!;
    }

    get valueSet() : ValueSet | undefined {
        return metaModel[ this._schema ].getValueSet( this._valueSetId );
    }

    get getValueSetValues() : PropertySetValue[] {
        return metaModel[ this._schema ].getValueSetValues( this._valueSetId ).map( value => {
            return {
                id         : value.id,
                valueSetId : value.valueSetId,
                statusId   : value.statusId,
                stringValue: value.value,
                value      : convertValueForType( value.value, this._valueType ),
                sortOrder  : value.sortOrder
            } as PropertySetValue;
        } );
    }

    get isKeySettable() : boolean {
        let retVal = false;

        if ( this._isEnumerated && ( !this._keyDefinition || this._keyDefinition.indexOf( '<key>' ) !== -1 || this._keyDefinition.indexOf( '<N>' ) === -1 ) ) {
            retVal = true;
        }

        return retVal;
    }

    get isKeyOrdered() : boolean {
        let retVal = false;

        if ( this._keyDefinition.indexOf( '<N>' ) !== -1 ) {
            retVal = true;
        }

        return retVal;
    }

    get isValueSettable() : boolean {
        let retVal = true;

        if ( this._isEnumerated && this._valueDefinition && this._valueDefinition.indexOf( '<value>' ) === -1 && this._valueDefinition.indexOf( '<N>' ) !== -1 ) {
            retVal = false;
        }

        return retVal;
    }

    get isValueOrdered() : boolean {
        let retVal = false;

        if ( this._valueDefinition.indexOf( '<N>' ) !== -1 ) {
            retVal = true;
        }

        return retVal;
    }

    get name() : string {
        return this.propertyType.name;
    }

    get fullName() : string {
        return `${this.referenceSet.name}:${this.name}`;
    }

    /**
     * The label for this
     */
    get labelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'referencePropertySet', this.id, 'typeLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = this.propertyType.name;
        }

        return retVal;
    }

    /**
     * The label for this
     */
    get label() : string {
        return __( this.labelMsgId );
    }

    /**
     * The label for this
     */
    get fullLabel() : string {
        return `${this.referenceSet.label}:${this.label}`;
    }

    /**
     * The key label for this
     */
    get keyLabelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'referencePropertySet', this.id, 'keyLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = 'YN.base.common.property.key';
        }

        return retVal;
    }

    /**
     * The key label for this
     */
    get keyLabel() : string {
        return __( this.keyLabelMsgId );
    }

    /**
     * The value label for this
     */
    get valueLabelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'referencePropertySet', this.id, 'valueLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = 'YN.base.common.property.value';
        }

        return retVal;
    }

    /**
     * The value label for this
     */
    get valueLabel() : string {
        return __( this.valueLabelMsgId );
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof ReferencePropertySetAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param schema                    The database schema the Item belongs to
     */
    constructor( attributes : ReferencePropertySetAttributes, schema : Schema = global.schema ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        this._schema = schema;

        this.referenceSetId = attributes.referenceSetId;
        this.propertyTypeId = attributes.propertyTypeId;

        if ( attributes.id ) {
            this.id = attributes.id;
        }
        else {
            this.id = ReferencePropertySet.uniqueIdCount--;
        }

        if ( attributes.valueSetId ) {
            this._valueSetId = attributes.valueSetId;
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.origin ) {
            this._origin = attributes.origin;
        }

        if ( attributes.module ) {
            this._module = attributes.module;
        }

        if ( attributes.isEnumerated !== undefined ) {
            this._isEnumerated = attributes.isEnumerated;
        }

        if ( attributes.isImported !== undefined ) {
            this._isImported = attributes.isImported;
        }

        if ( attributes.description ) {
            this._description = attributes.description;
        }

        if ( attributes.keyType ) {
            this._keyType = attributes.keyType;
        }
        if ( attributes.keyDefinition ) {
            this.validateKeyDefinition( attributes.keyDefinition );
            this._keyDefinition = attributes.keyDefinition;
        }
        this.parseKeyDefinition();
        if ( attributes.keyExample ) {
            this._keyExample = attributes.keyExample;
        }

        if ( attributes.valueType ) {
            this._valueType = attributes.valueType;
        }
        if ( attributes.valueDefinition ) {
            this.validateValueDefinition( attributes.valueDefinition );
            this._valueDefinition = attributes.valueDefinition;
        }
        this.parseValueDefinition();
        if ( attributes.valueExample ) {
            this._valueExample = attributes.valueExample;
        }
        if ( attributes.valueDefault ) {
            this._valueDefault = attributes.valueDefault;
        }
        if ( attributes.valueMin ) {
            this._valueMin = attributes.valueMin;
        }
        if ( attributes.valueMax ) {
            this._valueMax = attributes.valueMax;
        }
        if ( attributes.valueUnit ) {
            this._valueUnit = attributes.valueUnit;
        }

        if ( attributes.uuid ) {
            this.uuid = attributes.uuid;
        }

        if ( attributes.modifierUserId ) {
            this.modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            this.modificationTime = attributes.modificationTime;
        }

        if ( attributes.validFromTime ) {
            this.validFromTime = attributes.validFromTime;
        }
        if ( attributes.validToTime ) {
            this.validToTime = attributes.validToTime;
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ReferencePropertySetAttributes {
        // required attributes
        const retVal : ReferencePropertySetAttributes = {
            id            : this.id,
            referenceSetId: this.referenceSetId,
            propertyTypeId: this.propertyTypeId
        };

        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueSetId' ) ) {
            retVal.valueSetId = this._valueSetId;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'origin' ) ) {
            retVal.origin = this._origin;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'module' ) ) {
            retVal.module = this._module;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'description' ) ) {
            retVal.description = this._description;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'keyType' ) ) {
            retVal.keyType = this._keyType;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'keyDefinition' ) ) {
            retVal.keyDefinition = this._keyDefinition;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'keyExample' ) ) {
            retVal.keyExample = this._keyExample;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueType' ) ) {
            retVal.valueType = this._valueType;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueDefinition' ) ) {
            retVal.valueDefinition = this._valueDefinition;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueExample' ) ) {
            retVal.valueExample = this._valueExample;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueDefault' ) ) {
            retVal.valueDefault = this._valueDefault;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueMin' ) ) {
            retVal.valueMin = this._valueMin;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueMax' ) ) {
            retVal.valueMax = this._valueMax;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'valueUnit' ) ) {
            retVal.valueUnit = this._valueUnit;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'isEnumerated' ) ) {
            retVal.isEnumerated = this._isEnumerated;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'isImported' ) ) {
            retVal.isImported = this._isImported;
        }

        // not settable attributes
        if ( includeAttributes === 'all' ) {
            retVal.uuid = this.uuid;
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = this.modificationTime;
            }

            // only history attributes
            else {
                retVal.validFromTime = this.validFromTime;
                retVal.validToTime = this.validToTime;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `ReferencePropertySet '${this.fullName}' (ID: ${this.id})`;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() {
        if ( this._attributesModified.has( 'valueSetId' ) ) {
            this._valueSetId = this._attributesModified.get( 'valueSetId' ) as number;
        }

        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        if ( this._attributesModified.has( 'description' ) ) {
            this._description = this._attributesModified.get( 'description' ) as string;
        }

        if ( this._attributesModified.has( 'keyType' ) ) {
            this._keyType = this._attributesModified.get( 'keyType' ) as PropertyKeyType;
        }
        if ( this._attributesModified.has( 'keyDefinition' ) ) {
            this._keyDefinition = this._attributesModified.get( 'keyDefinition' ) as string;
        }
        if ( this._attributesModified.has( 'keyExample' ) ) {
            this._keyExample = this._attributesModified.get( 'keyExample' ) as string;
        }

        if ( this._attributesModified.has( 'valueType' ) ) {
            this._valueType = this._attributesModified.get( 'valueType' ) as PropertyValueType;
        }
        if ( this._attributesModified.has( 'valueDefinition' ) ) {
            this._valueDefinition = this._attributesModified.get( 'valueDefinition' ) as string;
        }
        if ( this._attributesModified.has( 'valueExample' ) ) {
            this._valueExample = this._attributesModified.get( 'valueExample' ) as string;
        }
        if ( this._attributesModified.has( 'valueDefault' ) ) {
            this._valueDefault = this._attributesModified.get( 'valueDefault' ) as string;
        }
        if ( this._attributesModified.has( 'valueMin' ) ) {
            this._valueMin = this._attributesModified.get( 'valueMin' ) as string;
        }
        if ( this._attributesModified.has( 'valueMax' ) ) {
            this._valueMax = this._attributesModified.get( 'valueMax' ) as string;
        }
        if ( this._attributesModified.has( 'valueUnit' ) ) {
            this._valueUnit = this._attributesModified.get( 'valueUnit' ) as string;
        }

        if ( this._attributesModified.has( 'isEnumerated' ) ) {
            this._isEnumerated = this._attributesModified.get( 'isEnumerated' ) as boolean;
        }

        if ( this._attributesModified.has( 'isImported' ) ) {
            this._isImported = this._attributesModified.get( 'isImported' ) as boolean;
        }

        this._attributesModified = new Map();
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : ReferencePropertySetAttributesSettable = {} ) : ReferencePropertySet {
        const clone : ReferencePropertySet = Object.assign( {}, this );

        // apply data
        if ( attributes.valueSetId ) {
            clone.valueSetId = attributes.valueSetId;
        }

        if ( attributes.statusId ) {
            clone.statusId = attributes.statusId;
        }

        if ( attributes.description ) {
            clone.description = attributes.description;
        }

        if ( attributes.keyType ) {
            clone.keyType = attributes.keyType;
        }
        if ( attributes.keyDefinition ) {
            clone.keyDefinition = attributes.keyDefinition;
        }
        if ( attributes.keyExample ) {
            clone.keyExample = attributes.keyExample;
        }

        if ( attributes.valueType ) {
            clone.valueType = attributes.valueType;
        }
        if ( attributes.valueDefinition ) {
            clone.valueDefinition = attributes.valueDefinition;
        }
        if ( attributes.valueExample ) {
            clone.valueExample = attributes.valueExample;
        }
        if ( attributes.valueDefault ) {
            clone.valueDefault = attributes.valueDefault;
        }
        if ( attributes.valueMin ) {
            clone.valueMin = attributes.valueMin;
        }
        if ( attributes.valueMax ) {
            clone.valueMax = attributes.valueMax;
        }
        if ( attributes.valueUnit ) {
            clone.valueUnit = attributes.valueUnit;
        }

        if ( attributes.isEnumerated ) {
            clone.isEnumerated = attributes.isEnumerated;
        }

        if ( attributes.isImported ) {
            clone.isImported = attributes.isImported;
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this.id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return this._statusId === metaModel[ this._schema ].getStatus( 'history' )?.id || this._statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id;
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof ReferencePropertySetAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    getStringKey( dataObject : { key : AttributeValue | undefined, orderNum : number } ) : string {
        return new Function( 'convertValueToString', 'with( convertValueToString ) return `' + this.keyToStringTemplate + '`' ).bind( dataObject, convertValueToString )().trim();
    }

    getStringValue( dataObject : { value : AttributeValue | undefined, orderNum : number } ) : string {
        return new Function( 'convertValueToString', 'with( convertValueToString ) return `' + this.valueToStringTemplate + '`' ).bind( dataObject, convertValueToString )().trim();
    }

    protected parseKeyDefinition() {
        if ( this._keyDefinition && this._keyDefinition !== '<key>' && ( this._keyDefinition.indexOf( '<N>' ) !== -1 || this._keyDefinition.indexOf( '<key>' ) !== -1 ) ) {
            this._keyToStringTemplate = this._keyDefinition
                                            .replaceAll( '<N>', '${\'0\'.repeat( 3 - String( this.orderNum ).length )}${this.orderNum}' )
                                            .replaceAll( '<key>', `\$\{convertValueToString( this.key, '${this._keyType}' )\}` );

            this._keyFromStringRegExp = `^${this._keyDefinition
                                                .replace( /[-\/\\^$*+?.()|[\]{}]/g, '\\$&' )        // escape all special reg exp chars
                                                .replace( '<N>', '(?<orderNum>[0-9]+)' )            // replace first <N>
                                                .replaceAll( '<N>', '[0-9]+' )                      // replace all other <N>
                //.replace( '<key>', '(?<key>.*)' )                   // replace first <key>
                //.replaceAll( /(?<!\?)(<key>)/g, '.*?' )}$`;         // replace all other <key>
                // BUGFIX for Safari - temporary placeholder, since SAFARI doesn't know look behind
                                                .replace( '<key>', '(?<keyPlaceholder>.*)' )        // replace first <key>
                                                .replaceAll( /<key>/g, '.*?' )                      // replace all other <key>
                                                .replace( 'keyPlaceholder', 'key' )}$`;             // replace valuePlaceholder with value
        }
        else {
            this._keyToStringTemplate = `\$\{convertValueToString( this.key, '${this._keyType}' )\}`;
            this._keyFromStringRegExp = undefined;
        }
    }

    protected parseValueDefinition() {
        if ( this._valueDefinition && this._valueDefinition !== '<value>' && ( this._valueDefinition.indexOf( '<N>' ) !== -1 || this._valueDefinition.indexOf( '<value>' ) !== -1 ) ) {
            this._valueToStringTemplate = this._valueDefinition
                                              .replaceAll( '<N>', '${\'0\'.repeat( 3 - String( this.orderNum ).length )}${this.orderNum}' )
                                              .replaceAll( '<value>', `\$\{convertValueToString( this.value, '${this._valueType}' )\}` );

            this._valueFromStringRegExp = `^${this._valueDefinition
                                                  .replace( /[-\/\\^$*+?.()|[\]{}]/g, '\\$&' )            // escape all special reg exp chars
                                                  .replace( '<N>', '(?<orderNum>[0-9]+)' )                // replace first <N>
                                                  .replaceAll( '<N>', '[0-9]+' )                          // replace all other <N>
                //.replace( '<value>', '(?<value>.*)' )                   // replace first <value>
                //.replaceAll( /(?<!\?)(<value>)/g, '.*?' )}$`;           // replace all other <value>
                // BUGFIX for Safari - temporary placeholder, since SAFARI doesn't know look behind
                                                  .replace( '<value>', '(?<valuePlaceholder>.*)' )        // replace first <value>, temporary with valuePlaceholder, since SAFARI doesn't know look behind
                                                  .replaceAll( /<value>/g, '.*?' )                        // replace all other <value>
                                                  .replace( 'valuePlaceholder', 'value' )}$`;             // replace valuePlaceholder with value
        }
        else {
            this._valueToStringTemplate = `\$\{convertValueToString( this.value, '${this._valueType}' )\}`;
            this._valueFromStringRegExp = undefined;
        }
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof ReferencePropertySetAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError( 'YN.base.dataObject.referencePropertySet.attributes.validation.couldNotChangeDeleted' );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError( 'YN.base.dataObject.referencePropertySet.attributes.validation.couldNotDeleteChanged' );
        }
    }

    /**
     * Validates the origin for this
     */
    protected validateOrigin( origin : string ) {
        this.checkStatus();

        if ( !origin.match( /^[A-Z]+$/ ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.origin.isNotUppercase' );
        }
    }

    /**
     * Validates the module for this
     */
    protected validateModule( module : string ) {
        this.checkStatus();
    }

    /**
     * Validates the isEnumerated for this
     */
    protected validateIsEnumerated( isEnumerated : boolean ) {
        this.checkStatus();

        if ( isEnumerated ) {
            this.validateKeyDefinition( this._keyDefinition );
            this.validateValueDefinition( this._valueDefinition );
        }
    }

    /**
     * Validates the defaultValue for this
     */
    protected validateDefaultValue( defaultValue : AttributeValue ) {
        this.checkStatus();

        const valueSet = this.valueSet;

        if ( valueSet ) {
            const rawValueSetValues = valueSet.values,
                  valueSetValues    = rawValueSetValues.map( ( valueSetValue : Value ) => {
                      return convertValueForType( valueSetValue.value, this._valueType );
                  } );

            if ( valueSetValues.indexOf( defaultValue ) !== -1 ) {
                throw new LocalizableError( 'YN.base.dataObject.referencePropertySet.attribute.validation.defaultValue.doesntMatchValueSet', { value: String( defaultValue ) } );
            }
        }

        // allow 'now' for DATE, TIME, TIMESTAMP (don't validate than)
        if ( !( defaultValue === 'now' && ( this._valueType === 'DATE' || this._valueType === 'TIME' || this._valueType === 'TIMESTAMP' ) ) ) {
            validateValueForType( defaultValue, this._valueType, true );
        }
    }

    /**
     * Validates the keyDefinition for this
     */
    protected validateKeyDefinition( keyDefinition : string ) {
        this.checkStatus();

        // enumerated
        if ( !this._isEnumerated ) {
            throw new LocalizableError( 'YN.base.dataObject.referencePropertySet.attributes.validation.keyDefinition.notValidForNonEnum' );
        }

        // keyDefinition must contain at least one of the strings <N> or <key>
        /*if ( keyDefinition && keyDefinition.indexOf( '<N>' ) === -1 && keyDefinition.indexOf( '<key>' ) === -1 ) {
            throw new LocalizableError( 'YN.base.dataObject.referencePropertySet.attributes.validation.keyDefinition.notValid' );
        }*/

        this.validateKeyAndValueDefinition( keyDefinition );
    }

    /**
     * Validates the valueDefinition for this
     */
    protected validateValueDefinition( valueDefinition : string ) {
        this.checkStatus();

        if ( this._isEnumerated ) {
            // valueDefinition must contain at least one of the strings <N> or <value>
            /*if ( valueDefinition && valueDefinition.indexOf( '<N>' ) === -1 && valueDefinition.indexOf( '<value>' ) === -1 ) {
                throw new LocalizableError( 'YN.base.dataObject.referencePropertySet.attributes.validation.valueDefinition.notValid' );
            }*/

            this.validateKeyAndValueDefinition( undefined, valueDefinition );
        }
    }

    protected validateKeyAndValueDefinition( keyDefinition? : string, valueDefinition? : string ) {
        if ( ( keyDefinition ?? this._keyDefinition ).indexOf( '<N>' ) !== -1 && ( valueDefinition ?? this._valueDefinition ).indexOf( '<N>' ) !== -1 ) {
            throw new LocalizableError( 'YN.base.dataObject.propertySet.attributes.validation.canNotOrderByKeyAndValue' );
        }
    }
}

export default ReferencePropertySet;
